import {
  AUTOMATION_LIMITS,
  AUTOMATION_PLACEHOLDERS,
  AUTOMATION_TRIGGERS,
  type AutomationPlaceholder,
  type AutomationRuleDto,
  type AutomationTriggerType,
} from '@fos/shared/contracts/automations';
import { AUTOMATION_ACTION_HINTS, AUTOMATION_ACTION_LABELS, AUTOMATION_TRIGGER_LABELS } from '@fos/shared/labels';
import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { ContextBanner, FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Combobox } from '../../../ui/Combobox';
import { TimeInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { Select } from '../../../ui/Select';
import { toast } from '../../../ui/Toast';
import { ClientPicker } from '../../clients/components/ClientPicker';
import { FULLSCREEN_ON_PHONE } from '../../mobile';
import {
  actionsFromForm,
  EMPTY_RULE_FORM,
  formFromRule,
  placeholdersFor,
  type RuleForm,
  ruleErrorKey,
  ruleFormIssues,
  taskHintFor,
  triggerFromForm,
  triggerUsesAssets,
  triggerUsesClients,
  withTrigger,
} from './automations-ui';

const TRIGGER_OPTIONS = AUTOMATION_TRIGGERS.map((t) => ({ value: t, label: AUTOMATION_TRIGGER_LABELS[t] }));
const PRIORITY_OPTIONS = [
  { value: 'normal' as const, label: 'Zwykły' },
  { value: 'high' as const, label: 'Wysoki' },
  { value: 'urgent' as const, label: 'Pilny' },
];
const DUE_OPTIONS = [
  { value: 'none', label: 'Bez terminu' },
  { value: '0', label: 'Na dziś' },
  { value: '1', label: 'Na jutro' },
  { value: '3', label: 'Za 3 dni' },
  { value: '7', label: 'Za tydzień' },
];
const ALL = '__all__';

/**
 * Edytor reguły (Ustawienia → Automatyzacje; czeka na projekt w Figmie): nazwa, wyzwalacz z filtrem (klient, zasób,
 * dni przed, godzina), akcje (podsumowanie / brief, zadanie z szablonem tytułu, „Zleć Claude” z instrukcją i limitem,
 * „Powiadom mnie”: pilne, push, mail na mój adres). Pola szablonu wstawia się przyciskami; w zleceniu dla Claude
 * tylko pola zaufane (bez treści maila). Zgoda na Claude jest osobno, w karcie reguły (owner/admin, hasło).
 */
export function RuleEditorDialog({
  open,
  onOpenChange,
  rule,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** edycja istniejącej reguły; brak = nowa */
  rule: AutomationRuleDto | null;
  onSaved?: (rule: AutomationRuleDto) => void;
}) {
  const utils = trpc.useUtils();
  const access = useAccess();
  const create = trpc.automations.create.useMutation();
  const update = trpc.automations.update.useMutation();
  const [form, setForm] = useState<RuleForm>(EMPTY_RULE_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(rule ? formFromRule(rule) : EMPTY_RULE_FORM);
    setErrors({});
    setFormError(null);
  }, [open, rule]);

  const canAgents = access.can('agents', 'edit');
  const canTasks = access.can('tasks', 'edit');
  const assets = trpc.maintenance.listAssets.useQuery(
    {},
    { enabled: open && triggerUsesAssets(form.trigger) && access.can('maintenance', 'view'), staleTime: 60_000 },
  );
  const assetOptions = useMemo(
    () => [
      { value: ALL, label: 'Wszystkie zasoby' },
      ...(assets.data ?? []).map((a) => ({ value: a.id, label: a.name, description: a.client.name })),
    ],
    [assets.data],
  );

  const set = (patch: Partial<RuleForm>) => setForm((f) => ({ ...f, ...patch }));
  const schedule = form.trigger === 'schedule.daily';
  const pending = create.isPending || update.isPending;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Podaj nazwę reguły';
    const actions = actionsFromForm(form);
    if (actions.length === 0) next.actions = 'Wybierz co najmniej jedną akcję';
    // te same reguły co serwer (np. pole szablonu, którego wyzwalacz nie zna) — błąd przy polu przed zapisem
    else for (const [key, message] of Object.entries(ruleFormIssues(form))) next[key] ??= message;
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    try {
      const trigger = triggerFromForm(form);
      const saved = rule
        ? await update.mutateAsync({ id: rule.id, version: rule.version, name: form.name.trim(), trigger, actions })
        : await create.mutateAsync({ name: form.name.trim(), trigger, actions });
      await utils.automations.invalidate();
      toast.success(rule ? 'Reguła zapisana' : 'Reguła dodana i włączona');
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      if (appErrorCode(err) === 'STEP_UP_REQUIRED') return;
      const mapped: Record<string, string> = {};
      for (const issue of validationIssues(err)) {
        const key = ruleErrorKey(issue.path);
        mapped[key] ??= issue.message;
      }
      if (Object.keys(mapped).length > 0) setErrors(mapped);
      setFormError(Object.keys(mapped).length > 0 ? null : errorMessage(err));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title={rule ? 'Edytuj regułę' : 'Nowa reguła'}
      className={FULLSCREEN_ON_PHONE}
      description="Gdy coś się wydarzy, Fravity OS zrobi to za Ciebie — z Twoimi uprawnieniami."
    >
      <form onSubmit={submit} noValidate className="flex flex-col gap-5" aria-label="Reguła automatyzacji">
        {rule?.claude.allowed && (
          <ContextBanner tone="info">
            Zmiana wyzwalacza, filtrów albo zlecenia dla Claude cofnie zgodę na zlecanie pracy Claude.
          </ContextBanner>
        )}
        <Field label="Nazwa" error={errors.name}>
          <Input
            autoFocus
            value={form.name}
            maxLength={200}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="np. Awaria strony → powiadom mnie"
          />
        </Field>

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-2 text-label text-fg">Kiedy</legend>
          <Field label="Wyzwalacz">
            <Select<AutomationTriggerType>
              value={form.trigger}
              onValueChange={(v) => setForm((f) => withTrigger(f, v))}
              options={TRIGGER_OPTIONS}
            />
          </Field>
          {triggerUsesClients(form.trigger) && (
            <Field label="Klient" hint="Puste = każdy klient, którego widzisz">
              <ClientPicker
                value={form.clientId}
                onChange={(clientId) => set({ clientId })}
                allowNone
                noneLabel="Wszyscy klienci"
                placeholder="Wszyscy klienci"
              />
            </Field>
          )}
          {triggerUsesAssets(form.trigger) && (
            <Field label="Zasób (strona, serwer)">
              <Combobox
                value={form.assetId ?? ALL}
                onValueChange={(v) => set({ assetId: v === ALL ? null : v })}
                options={assetOptions}
                placeholder="Wszystkie zasoby"
              />
            </Field>
          )}
          {form.trigger === 'renewal.due' && (
            <Field label="Ile dni przed terminem" hint="Jedno przypomnienie na termin płatności albo odnowienia">
              <Input
                type="number"
                min={0}
                max={90}
                value={String(form.daysBefore)}
                onChange={(e) => set({ daysBefore: Math.max(0, Math.min(90, Number(e.target.value) || 0)) })}
                className="w-28"
              />
            </Field>
          )}
          {form.trigger === 'task.overdue' && (
            <Checkbox
              checked={form.onlyMine}
              onCheckedChange={(onlyMine) => set({ onlyMine })}
              label="Tylko moje zadania (gdy jestem wykonawcą)"
            />
          )}
          {schedule && (
            <div className="flex flex-wrap items-end gap-4">
              <Field label="Godzina (czas polski)" error={errors.trigger}>
                <TimeInput value={form.time} onValueChange={(time) => set({ time })} size="m" className="w-28" />
              </Field>
              <Checkbox
                checked={form.weekdaysOnly}
                onCheckedChange={(weekdaysOnly) => set({ weekdaysOnly })}
                label="Tylko w dni robocze"
                className="pb-1.5"
              />
            </div>
          )}
        </fieldset>

        <fieldset className="flex flex-col gap-1">
          <legend className="mb-2 text-label text-fg">Co zrobić</legend>
          {errors.actions && <FormMessage>{errors.actions}</FormMessage>}
          {schedule ? (
            <ActionToggle
              checked={form.brief}
              onChange={(brief) => set({ brief, notify: brief ? { ...form.notify, on: true } : form.notify })}
              type="brief"
            />
          ) : (
            <ActionToggle
              checked={form.summary}
              onChange={(summary) => set({ summary, notify: summary ? { ...form.notify, on: true } : form.notify })}
              type="summary"
            />
          )}
          <ActionToggle
            checked={form.task.on}
            onChange={(on) => set({ task: { ...form.task, on } })}
            type="create_task"
            hint={taskHintFor(form.trigger)}
            disabled={!canTasks}
            note={!canTasks ? 'Wymaga modułu Zadania (Edycja)' : undefined}
          >
            <Field label="Tytuł zadania" error={errors.taskTitle}>
              <Input
                value={form.task.title}
                maxLength={AUTOMATION_LIMITS.titleMax}
                onChange={(e) => set({ task: { ...form.task, title: e.target.value } })}
              />
            </Field>
            <PlaceholderChips
              items={placeholdersFor(form.trigger)}
              onPick={(p) => set({ task: { ...form.task, title: `${form.task.title}{${p}}` } })}
            />
            <div className="flex flex-wrap gap-4">
              <Field label="Termin">
                <Select
                  value={form.task.dueInDays === null ? 'none' : String(form.task.dueInDays)}
                  onValueChange={(v) => set({ task: { ...form.task, dueInDays: v === 'none' ? null : Number(v) } })}
                  options={DUE_OPTIONS}
                  className="w-40"
                />
              </Field>
              <Field label="Priorytet">
                <Select
                  value={form.task.priority}
                  onValueChange={(priority) => set({ task: { ...form.task, priority } })}
                  options={PRIORITY_OPTIONS}
                  className="w-36"
                />
              </Field>
            </div>
            <Checkbox
              checked={form.task.assignToMe}
              onCheckedChange={(assignToMe) => set({ task: { ...form.task, assignToMe } })}
              label="Przypisz do mnie"
            />
          </ActionToggle>
          <ActionToggle
            checked={form.claude.on}
            onChange={(on) => set({ claude: { ...form.claude, on } })}
            type="delegate_claude"
            disabled={!canAgents}
            note={
              !canAgents
                ? 'Wymaga modułu Agenci (Edycja)'
                : 'Działa dopiero po zgodzie właściciela albo administratora w tej regule.'
            }
          >
            <Field
              label="Instrukcja dla Claude"
              hint="Tylko pola zaufane — treść maili klientów nigdy nie trafia do zlecenia."
              error={errors.claudeInstruction}
            >
              <Textarea
                rows={4}
                value={form.claude.instruction}
                maxLength={AUTOMATION_LIMITS.instructionMax}
                onChange={(e) => set({ claude: { ...form.claude, instruction: e.target.value } })}
              />
            </Field>
            <PlaceholderChips
              items={placeholdersFor(form.trigger, true)}
              onPick={(p) => set({ claude: { ...form.claude, instruction: `${form.claude.instruction} {${p}}` } })}
            />
            <Field label="Najwyżej zleceń na dobę">
              <Input
                type="number"
                min={1}
                max={AUTOMATION_LIMITS.claudePerDayMax}
                value={String(form.claude.dailyLimit)}
                onChange={(e) =>
                  set({
                    claude: {
                      ...form.claude,
                      dailyLimit: Math.max(1, Math.min(AUTOMATION_LIMITS.claudePerDayMax, Number(e.target.value) || 1)),
                    },
                  })
                }
                className="w-24"
              />
            </Field>
          </ActionToggle>
          <ActionToggle
            checked={form.notify.on}
            onChange={(on) => set({ notify: { ...form.notify, on } })}
            type="notify"
          >
            <Checkbox
              checked={form.notify.urgent}
              onCheckedChange={(urgent) => set({ notify: { ...form.notify, urgent } })}
              label="Pilne (w Inboxie jako Pilne; push także w godzinach ciszy, jeśli tak ustawisz w Powiadomieniach)"
            />
            {!form.notify.urgent && (
              <Checkbox
                checked={form.notify.push}
                onCheckedChange={(push) => set({ notify: { ...form.notify, push } })}
                label="Push na telefon i komputer (poza godzinami ciszy)"
              />
            )}
            <Checkbox
              checked={form.notify.email}
              onCheckedChange={(email) => set({ notify: { ...form.notify, email } })}
              label="Także mail na mój adres"
            />
          </ActionToggle>
        </fieldset>

        {formError && <FormMessage>{formError}</FormMessage>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button type="submit" variant="primary" loading={pending}>
            {rule ? 'Zapisz' : 'Dodaj regułę'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function ActionToggle({
  checked,
  onChange,
  type,
  hint,
  disabled,
  note,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  type: keyof typeof AUTOMATION_ACTION_LABELS;
  /** podpowiedź zależna od wyzwalacza (domyślnie `AUTOMATION_ACTION_HINTS`) */
  hint?: string;
  disabled?: boolean;
  note?: string;
  children?: ReactNode;
}) {
  return (
    <div className="border-b border-line py-3 last:border-b-0">
      <Checkbox
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        label={<span className="font-medium text-fg">{AUTOMATION_ACTION_LABELS[type]}</span>}
      />
      <p className="mt-0.5 ml-6 text-caption text-fg-muted">{hint ?? AUTOMATION_ACTION_HINTS[type]}</p>
      {note && <p className="mt-0.5 ml-6 text-caption text-fg-secondary">{note}</p>}
      {checked && children && <div className="mt-3 ml-6 flex flex-col gap-3">{children}</div>}
    </div>
  );
}

function PlaceholderChips({
  items,
  onPick,
}: {
  items: readonly AutomationPlaceholder[];
  onPick: (p: AutomationPlaceholder) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="-mt-1 flex flex-wrap items-center gap-1.5">
      <span className="text-caption text-fg-muted">Wstaw:</span>
      {items.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onPick(p)}
          title={AUTOMATION_PLACEHOLDERS[p].label}
          className="rounded-md border border-line bg-canvas px-1.5 py-0.5 font-mono text-micro text-fg-secondary hover:bg-surface focus-visible:outline-2 focus-visible:outline-accent"
        >
          {`{${p}}`}
        </button>
      ))}
    </div>
  );
}
